<template>
  <div class="card accordion position-relative" v-hide="{ hide: category.removed }" @hideComplete="hideComplete">
    <header class="card-header" role="tab">
      <section class="mb-0 mt-0">
        <div
          class="collapsed accordion__heading"
          role="menu"
          aria-expanded="false"
          :aria-controls="'skill-category-' + category.id"
        >
          <div data-bs-toggle="collapse" :data-bs-target="'#skill-category-' + category.id" class="flex-grow-1">
            <div class="d-flex align-items-center">
              <div class="accordion__grip">
                <FontAwesomeIcon icon="fa-light fa-grip-lines-vertical" />
              </div>
              <div class="flex-grow-1">
                <span class="badge bg-info me-2">{{ category.code }}</span> {{ category.name }}
                <div class="fw-light mt-2">
                  {{ Object.keys(category.skillsAccepted).length }} {{ $t("training.rncp.rncp_skills") }} +
                  {{ Object.keys(category.skillsSuggested).length }}
                  {{ $t("training.rncp.ai_suggestions") }}
                </div>
              </div>
            </div>
          </div>
          <div class="d-flex align-items-center mt-2 mt-md-0">
            <button
              class="btn-icon editCategory me-2"
              @click="edit"
              v-tooltip="{
                title: stringManager().globalTranslation('edit', 'training-skill-category', 'studea_manager', 1, true),
              }"
            >
              <FontAwesomeIcon icon="fa-light fa-pen" />
            </button>
            <button
              class="btn-icon removeCategory me-2"
              @click="remove"
              v-tooltip="{
                title: stringManager().globalTranslation(
                  'delete',
                  'training-skill-category',
                  'studea_manager',
                  1,
                  true,
                ),
              }"
            >
              <FontAwesomeIcon icon="fa-light fa-trash" />
            </button>
            <div class="icons" data-bs-toggle="collapse" :data-bs-target="'#skill-category-' + category.id">
              <FontAwesomeIcon icon="fa-light fa-angle-down" />
            </div>
          </div>
        </div>
      </section>
    </header>
    <div
      :id="'skill-category-' + category.id"
      class="collapse"
      :aria-labelledby="'skill-category-' + category.id"
      data-bs-parent="#skill-category-accordion"
    >
      <div class="card-body accordion__body">
        <div class="row">
          <div class="col-lg-6">
            <div class="d-flex flex-column justify-content-between h-100 position-relative">
              <div>
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <h3>{{ $t("training.rncp.skills_related") }}</h3>
                  <button
                    class="btn btn-icon addSkill me-2"
                    @click="addSkill"
                    v-tooltip="{
                      title: stringManager().globalTranslation(
                        'add',
                        trainingSkillConstante.OBJECT_TYPE_SKILL,
                        'studea_manager',
                        1,
                        true,
                      ),
                    }"
                  >
                    <FontAwesomeIcon icon="fa-light fa-plus" />
                  </button>
                </div>
                <template v-if="category.skillsAccepted && Object.keys(category.skillsAccepted).length">
                  <VueDraggableNext
                    :animation="200"
                    :delay="200"
                    :delay-on-touch-only="true"
                    :list="category.skillsAccepted"
                    handle=".skill__grip"
                    @change="sortSkill"
                  >
                    <template v-for="skill in category.skillsAccepted" :key="skill.id">
                      <TrainingSkillSkillComponent
                        :skill="skill"
                        @editSkill="editSkill"
                        @removed="skillRemoved"
                        @hideComplete="skillRemovedHideComplete"
                        @showDetails="showDetails"
                      />
                    </template>
                  </VueDraggableNext>
                </template>
                <AlertBlock v-else :text="$t('training.rncp.alert.empty_skills')" type="info" />
              </div>

              <div class="suggestions" v-if="ia">
                <div class="suggestions__heading">
                  <h3>{{ $t("training.rncp.ai_suggestions_related_skills") }}</h3>

                  <button
                    class="btn btn-purple refreshSuggestion"
                    @click="refreshSuggestions(trainingSkillConstante.OBJECT_TYPE_SKILL)"
                  >
                    <FontAwesomeIcon class="me-2" icon="fa-light fa-arrows-rotate" />
                    {{ $t("training.rncp.relaunch_ai") }}
                  </button>
                </div>

                <div class="suggestions__list">
                  <template v-if="category.skillsSuggested && Object.keys(category.skillsSuggested).length">
                    <template v-for="skill in category.skillsSuggested" :key="skill.id">
                      <TrainingSkillSuggestionComponent
                        :object="skill"
                        :type="trainingSkillConstante.OBJECT_TYPE_SKILL"
                        :targetChoices="targetChoices"
                        :category="category"
                        @decline="suggestionDeclined"
                        @accept="suggestionAccepted"
                        @hideComplete="suggestionDeclinedHideComplete"
                        @showDetails="showDetails"
                      />
                    </template>
                  </template>
                  <AlertBlock v-else :text="$t('training.rncp.alert.empty_suggestions')" type="info" />
                </div>
                <BlockLoader :loading="skillSuggestionLoading" />
              </div>
              <BlockLoader :loading="category.skillColumnLoading" />
            </div>
          </div>
          <div class="col-lg-6 mt-3 mt-lg-0">
            <div class="missions">
              <div class="d-flex flex-column justify-content-between h-100 position-relative">
                <div>
                  <div class="missions__heading mb-3">
                    <h3>{{ $t("training.rncp.missions_related") }}</h3>

                    <button
                      class="btn addMission btn-icon"
                      @click="addMission"
                      v-tooltip="{
                        title: stringManager().globalTranslation('add', 'mission', 'studea_manager', 1, true),
                      }"
                    >
                      <FontAwesomeIcon class="me-2" icon="fa-light fa-plus" />
                    </button>
                  </div>

                  <div
                    class="missions__list"
                    v-if="category.missionsAccepted && Object.keys(category.missionsAccepted).length"
                  >
                    <VueDraggableNext
                      :animation="200"
                      :delay="200"
                      :delay-on-touch-only="true"
                      :list="category.missionsAccepted"
                      handle=".mission__grip"
                      @change="sortMission"
                    >
                      <template v-for="mission in category.missionsAccepted" :key="mission.id">
                        <TrainingSkillMissionComponent
                          :mission="mission"
                          @editMission="editMission"
                          @removed="missionRemoved"
                          @hideComplete="missionRemovedHideComplete"
                          @showDetails="showDetails"
                        />
                      </template>
                    </VueDraggableNext>
                  </div>
                  <AlertBlock v-else :text="$t('training.rncp.alert.empty_missions')" type="info" />
                </div>

                <div class="suggestions" v-if="ia">
                  <div class="suggestions__heading">
                    <h4>{{ $t("training.rncp.ai_suggestions_related_missions") }}</h4>

                    <button class="btn btn-purple refreshSuggestionMission" @click="refreshSuggestions('mission')">
                      <FontAwesomeIcon class="me-2" icon="fa-light fa-arrows-rotate" />
                      {{ $t("training.rncp.relaunch_ai") }}
                    </button>
                  </div>

                  <div class="suggestions__list">
                    <template v-if="category.missionsSuggested && Object.keys(category.missionsSuggested).length">
                      <template v-for="mission in category.missionsSuggested" :key="mission.id">
                        <TrainingSkillSuggestionComponent
                          :object="mission"
                          type="mission"
                          :targetChoices="targetChoices"
                          :category="category"
                          @decline="suggestionDeclined"
                          @accept="suggestionAccepted"
                          @hideComplete="suggestionDeclinedHideComplete"
                          @showDetails="showDetails"
                        />
                      </template>
                    </template>
                    <AlertBlock v-else :text="$t('training.rncp.alert.empty_suggestions')" type="info" />
                  </div>
                  <BlockLoader :loading="missionSuggestionLoading" />
                </div>
                <BlockLoader :loading="category.missionColumnLoading" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <BlockLoader :loading="loading || category.loading" />
  </div>
</template>

<script>
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import TrainingSkillSkillComponent from "@/components/training/skill/TrainingSkillSkillComponent.vue";
import TrainingSkillSuggestionComponent from "@/components/training/skill/TrainingSkillSuggestionComponent.vue";
import TrainingSkillMissionComponent from "@/components/training/skill/TrainingSkillMissionComponent.vue";
import { VueDraggableNext } from "vue-draggable-next";
import { trainingManager } from "@/services/training/training-manager";
import BlockLoader from "@/components/blocks/BlockLoader.vue";
import AlertBlock from "@/components/blocks/AlertBlock.vue";
import { notificationManager } from "@/services/utilities/notification-manager";
import { stringManager } from "@/services/utilities/string-manager";
import { trainingSkillConstante } from "@/constantes/trainingSkill";

export default {
  name: "TrainingSkillCategoryComponent",
  computed: {
    trainingSkillConstante() {
      return trainingSkillConstante;
    },
  },
  components: {
    AlertBlock,
    BlockLoader,
    VueDraggableNext,
    TrainingSkillMissionComponent,
    TrainingSkillSuggestionComponent,
    TrainingSkillSkillComponent,
    FontAwesomeIcon,
  },
  data() {
    return {
      loading: false,
      missionSuggestionLoading: false,
      skillSuggestionLoading: false,
    };
  },
  props: {
    category: {
      type: Object,
      required: true,
    },
    targetChoices: {
      type: Array,
      required: true,
    },
    ia: {
      type: Boolean,
      required: true,
    },
  },
  emits: [
    "editCategory",
    "addSkill",
    "editSkill",
    "editMission",
    "addMission",
    "removed",
    "skillRemoved",
    "accepted",
    "refreshSuggestions",
    "hideComplete",
    "showDetails",
  ],
  methods: {
    stringManager() {
      return stringManager;
    },
    sortSkill(e) {
      if (e.moved) {
        const id = e.moved.element.id;
        const position = e.moved.newIndex;
        trainingManager.sort(
          id,
          this.$route.params.trainingId,
          position,
          trainingSkillConstante.OBJECT_TYPE_SKILL,
          trainingSkillConstante.OBJECT_TYPE_SKILL,
        );
      }
    },

    sortMission(e) {
      if (e.moved) {
        const id = e.moved.element.id;
        const position = e.moved.newIndex;
        trainingManager.sort(
          id,
          this.$route.params.trainingId,
          position,
          trainingSkillConstante.OBJECT_TYPE_MISSION,
          trainingSkillConstante.OBJECT_TYPE_MISSION,
        );
      }
    },

    addSkill() {
      this.$emit("addSkill", { category: this.category });
    },

    edit() {
      this.$emit("editCategory", { category: this.category });
    },

    editSkill(payload) {
      this.$emit("editSkill", { ...payload, ...{ category: this.category } });
    },

    editMission(payload) {
      this.$emit("editMission", { ...payload, ...{ category: this.category } });
    },

    addMission() {
      this.$emit("addMission", { category: this.category });
    },

    showDetails(payload) {
      this.$emit("showDetails", payload);
    },

    async remove() {
      this.loading = true;
      const done = await trainingManager.remove(
        this.category.id,
        this.$route.params.trainingId,
        trainingSkillConstante.OBJECT_TYPE_SKILL + "/category",
        "training-skill-category",
      );
      if (done && done.success) {
        this.$emit("removed", { objectId: this.category.id, key: "categories" });
      }
      this.loading = false;
    },

    hideComplete() {
      this.$emit("hideComplete", { objectId: this.category.id, key: "categories" });
    },

    skillRemoved(removedSkill) {
      removedSkill.removed = true;
    },

    skillRemovedHideComplete(removedSkill) {
      this.$emit("hideComplete", { objectId: removedSkill.id, key: "skillsAccepted", categoryId: this.category.id });
    },

    missionRemoved(removedMission) {
      removedMission.removed = true;
    },

    missionRemovedHideComplete(removedMission) {
      this.$emit("hideComplete", {
        objectId: removedMission.id,
        key: "missionsAccepted",
        categoryId: this.category.id,
      });
    },

    suggestionDeclined(payload) {
      payload.object.removed = true;
    },

    suggestionDeclinedHideComplete(payload) {
      const key = payload.type + "sSuggested";
      this.$emit("hideComplete", { objectId: payload.object.id, key: key, categoryId: this.category.id });
    },

    suggestionAccepted(payload) {
      this.$emit("accepted", payload);
    },

    async refreshSuggestions(type) {
      if (type === trainingSkillConstante.OBJECT_TYPE_SKILL) {
        this.skillSuggestionLoading = true;
      } else if (type === trainingSkillConstante.OBJECT_TYPE_MISSION) {
        this.missionSuggestionLoading = true;
      } else {
        return;
      }

      const suggestions =
        type === trainingSkillConstante.OBJECT_TYPE_SKILL
          ? this.category.skillsSuggested
          : this.category.missionsSuggested;
      const firstSuggestion = suggestions && suggestions.length ? suggestions[0] : null;
      if (!firstSuggestion) {
        this.skillSuggestionLoading = this.missionSuggestionLoading = false;
        return;
      }

      try {
        const response = await trainingManager.enrich(this.$route.params.trainingId, this.category.id, type);
        if (response) {
          const key = type + "sSuggested";
          if (Array.isArray(response)) {
            this.$emit("refreshSuggestions", { key: key, categoryId: this.category.id, objects: response });
          } else if (response.success) {
            this.$emit("hideComplete", { objectId: firstSuggestion.id, key: key, categoryId: this.category.id });
          }
        }
      } catch (error) {
        notificationManager.showNotification("error", this.$t("training.rncp.alert.error"));
      } finally {
        this.skillSuggestionLoading = this.missionSuggestionLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.accordion {
  &__card + &__card {
    margin-top: 15px;
  }

  &.card {
    overflow: hidden;
    .card-header {
      border-bottom: none;
      padding: 0;
      cursor: pointer;
    }
  }

  &__body {
    .col-lg-6 {
      @include up-to-md {
        padding: 0 5px;
      }
    }
  }

  &__grip {
    margin-right: 18px;
    cursor: grab;
  }

  &__heading {
    padding: 20px;
    display: flex;
    position: relative;
    font-weight: 700;
    color: var(--primary-color);
    justify-content: space-between;
    align-items: center;
    background: $light-blue;

    @include dark-theme {
      background: $m-color_7;
      color: $white;
    }

    @include up-to-md {
      padding: 20px 10px;
      flex-direction: column;
      align-items: flex-start;
    }

    .accordion-icon {
      margin-right: 8px;
      font-size: 16px;
    }

    .icons {
      transform: rotate(180deg);
      &.collapsed {
        transform: rotate(0deg);
      }
    }
  }
}

.suggestions {
  border: 1px dashed $ia-color;
  padding: 20px;
  border-radius: $radius;
  margin-top: 20px;
  background: $ia-color-bg;

  @include dark-theme {
    background: $m-color_18;
    color: $white;
  }

  @include up-to-md {
    padding: 15px 10px;
  }

  &__heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: $ia-color;

    @include up-to-md {
      flex-direction: column;
      align-items: flex-start;
    }
  }

  &__list {
    margin-top: 20px;
  }
}

.missions {
  height: 100%;
  &__heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
}

.editCategory {
  @include up-to-md {
    margin-left: 15px;
  }
}
</style>
